import type { MallStatItem, MallStats } from "@mall-mart/shared";
import { relativeSoldTime } from "../util/relativeTime.js";

/**
 * Mall stats panel: the same live commerce info the atrium jumbotron cycles
 * through (last sold, sell goal, most bought), available on demand from a
 * side button so players don't have to walk back to the fountain. Opens via
 * the MALL STATS side button or the M key.
 */
export class MallStatsPanel {
  private el: HTMLElement;
  private getStats: () => MallStats | null;

  constructor(elementId: string, buttonId: string, getStats: () => MallStats | null) {
    const found: HTMLElement | null = document.getElementById(elementId);
    if (found === null) {
      throw new Error(`MallStatsPanel element #${elementId} not found`);
    }
    this.el = found;
    this.getStats = getStats;

    const button: HTMLElement | null = document.getElementById(buttonId);
    if (button === null) {
      throw new Error(`MallStatsPanel button #${buttonId} not found`);
    }
    button.addEventListener("click", () => this.toggle());

    document.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Escape" && !this.el.hidden) {
        this.close();
      }
    });
  }

  open(): void {
    this.render();
    this.el.hidden = false;
  }

  close(): void {
    this.el.hidden = true;
  }

  toggle(): void {
    if (this.el.hidden) {
      this.open();
    } else {
      this.close();
    }
  }

  isOpen(): boolean {
    return !this.el.hidden;
  }

  private render(): void {
    this.el.replaceChildren();

    const closeBtn: HTMLButtonElement = document.createElement("button");
    closeBtn.className = "sp-close";
    closeBtn.textContent = "✕";
    closeBtn.addEventListener("click", () => this.close());
    this.el.appendChild(closeBtn);

    const h2: HTMLHeadingElement = document.createElement("h2");
    h2.textContent = "Mall Mart Live";
    this.el.appendChild(h2);

    const stats: MallStats | null = this.getStats();
    if (stats === null) {
      const empty: HTMLDivElement = document.createElement("div");
      empty.className = "sp-empty";
      empty.textContent = "Stats are still loading, check back in a moment.";
      this.el.appendChild(empty);
      return;
    }

    if (stats.lastSold !== null) {
      this.renderItemSection("Just Sold", stats.lastSold);
    }
    if (stats.sellGoal !== null) {
      this.renderSellGoal(stats.sellGoal.itemsSold, stats.sellGoal.itemsNeeded, stats.sellGoal.itemsRemaining, stats.sellGoal.progressPercent);
    }
    if (stats.mostBought !== null) {
      this.renderItemSection("Most Bought", stats.mostBought);
    }
    if (stats.lastSold === null && stats.sellGoal === null && stats.mostBought === null) {
      const empty: HTMLDivElement = document.createElement("div");
      empty.className = "sp-empty";
      empty.textContent = "No mall stats available right now.";
      this.el.appendChild(empty);
    }
  }

  private renderItemSection(heading: string, item: MallStatItem): void {
    const section: HTMLDivElement = document.createElement("div");
    section.className = "sp-section";

    const h3: HTMLHeadingElement = document.createElement("h3");
    h3.textContent = heading;
    section.appendChild(h3);

    const row: HTMLDivElement = document.createElement("div");
    row.className = "sp-item";

    if (item.imageUrl !== "") {
      const img: HTMLImageElement = document.createElement("img");
      img.src = item.imageUrl;
      img.alt = item.title;
      img.loading = "lazy";
      row.appendChild(img);
    }

    const info: HTMLDivElement = document.createElement("div");
    const title: HTMLDivElement = document.createElement("div");
    title.className = "sp-title";
    title.textContent = item.title;
    info.appendChild(title);
    if (item.unitsSold !== undefined) {
      const sold: HTMLDivElement = document.createElement("div");
      sold.className = "sp-sold";
      sold.textContent = `${item.unitsSold} sold`;
      info.appendChild(sold);
    }
    const soldAgo: string | null = relativeSoldTime(item.soldAt);
    if (soldAgo !== null) {
      const when: HTMLDivElement = document.createElement("div");
      when.className = "sp-sold-when";
      when.textContent = `Bought ${soldAgo}`;
      info.appendChild(when);
    }
    row.appendChild(info);

    section.appendChild(row);
    this.el.appendChild(section);
  }

  private renderSellGoal(sold: number, needed: number, remaining: number, percent: number): void {
    const section: HTMLDivElement = document.createElement("div");
    section.className = "sp-section";

    const h3: HTMLHeadingElement = document.createElement("h3");
    h3.textContent = "Mall Sell Goal";
    section.appendChild(h3);

    const bar: HTMLDivElement = document.createElement("div");
    bar.className = "sp-bar";
    const fill: HTMLDivElement = document.createElement("div");
    fill.className = "sp-bar-fill";
    fill.style.width = `${Math.min(Math.max(percent, 0), 100)}%`;
    bar.appendChild(fill);
    section.appendChild(bar);

    const detail: HTMLDivElement = document.createElement("div");
    detail.className = "sp-goal-detail";
    detail.textContent = `${sold} / ${needed} items sold, ${remaining} to go (${percent.toFixed(1)}%)`;
    section.appendChild(detail);

    this.el.appendChild(section);
  }
}
